import React, { createContext, useContext, useState, useRef, useCallback } from 'react'
import axios from 'axios'

const API  = process.env.REACT_APP_API_URL || 'http://localhost:8000'
const CalebContext = createContext(null)

export function CalebProvider({ children }) {
  const [isOpen,   setIsOpen]   = useState(false)
  const [context,  setContext]  = useState(null)   // { type, patientId, patientName, injuryName, exerciseName, extra }
  const [history,  setHistory]  = useState([])
  const [input,    setInput]    = useState('')
  const [loading,  setLoading]  = useState(false)
  const [analysis, setAnalysis] = useState(null)   // structured injury analysis card
  const endRef = useRef(null)

  const open = useCallback((cfg = {}) => {
    // Always clear history and analysis when panel is opened fresh
    setHistory([])
    setAnalysis(null)
    setContext(cfg)
    setIsOpen(true)

    const doAutoSend = (autoMsg, ctxType, ctxExtra) => {
      setTimeout(() => {
        const userMsg = { role: 'user', content: autoMsg }
        setHistory([userMsg])
        setLoading(true)
        axios.post(API + '/ai/chat', {
          patient_id:    cfg.patientId || null,
          message:       autoMsg,
          history:       [],
          context_type:  ctxType,
          context_extra: ctxExtra || {},
        }).then(res => {
          setHistory([userMsg, { role: 'assistant', content: res.data.response }])
          setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 50)
        }).catch(err => {
          const raw    = err?.response?.data?.detail || err?.message || 'Something went wrong.'
          const status = err?.response?.status
          let msg = raw
          if (status === 404 || String(raw).toLowerCase().includes('not found')) {
            msg = 'The AI service endpoint isn\'t available yet — the backend needs to be deployed with the new AI routes.'
          } else if (status === 503 || String(raw).toLowerCase().includes('api_key')) {
            msg = 'Caleb\'s API key is not configured on the server yet. Ask your admin to add the ANTHROPIC_API_KEY to the server environment.'
          } else if (!err.response && String(raw).toLowerCase().includes('network')) {
            msg = 'Cannot reach the server. Please check your connection or try again in a moment.'
          }
          setHistory([userMsg, { role: 'assistant', content: ` ${msg}` }])
        }).finally(() => setLoading(false))
      }, 80)
    }

    // Auto-explain for injury context
    if (cfg.type === 'injury' && cfg.injuryName) {
      const injName = cfg.injuryName
      const patientNote = cfg.patientName ? ` for a patient named ${cfg.patientName}` : ''
      const autoMsg = `Please explain "${injName}"${patientNote} in simple, friendly language a patient can easily understand. Describe what it is, what causes it, and what they can expect from physical therapy — no medical jargon.`
      doAutoSend(autoMsg, 'injury', { injuryName: injName, layer: cfg.extra?.layer })
    }

    // Auto-greeting for patient context (therapist opening Caleb for a patient)
    if (cfg.type === 'patient' && cfg.patientId) {
      const patientName = cfg.patientName || 'this patient'
      const injury = cfg.extra?.injuryName || cfg.extra?.injuryType || cfg.extra?.diagnosis || ''
      const injuryNote = injury ? ` presenting with ${injury}` : ''
      const autoMsg = `I'm reviewing ${patientName}${injuryNote}. Give me a quick clinical summary and any key things I should watch for in today's session.`
      doAutoSend(autoMsg, 'patient', cfg.extra || {})
    }

    // Auto-greeting for exercise context
    if (cfg.type === 'exercise' && cfg.exerciseName) {
      const exName = cfg.exerciseName
      const autoMsg = `Tell me about the "${exName}" exercise — what muscles does it work, what's the correct form, and are there any common mistakes I should avoid?`
      doAutoSend(autoMsg, 'exercise', { exerciseName: exName })
    }
  }, [])

  const close = useCallback(() => setIsOpen(false), [])

  // Load structured injury analysis (used by InjuryExplorer)
  const loadInjuryAnalysis = useCallback(async (injuryName, patientId = null) => {
    setAnalysis(null)
    setLoading(true)
    try {
      const res = await axios.post(API + '/ai/injury-analysis', { injury_name: injuryName, patient_id: patientId })
      setAnalysis(res.data)
    } catch (err) {
      const status = err?.response?.status
      const raw = err?.response?.data?.detail || err?.message || 'Could not load analysis.'
      let msg = raw
      if (status === 404) msg = 'AI analysis endpoint not deployed yet. Deploy the latest backend to enable this feature.'
      else if (status === 503) msg = 'ANTHROPIC_API_KEY not configured on server. Add it to the EC2 .env file.'
      setAnalysis({ error: msg })
    } finally {
      setLoading(false)
    }
  }, [])

  const send = useCallback(async (messageOverride) => {
    const msg = (messageOverride || input).trim()
    if (!msg || loading) return
    setInput('')

    const userMsg = { role: 'user', content: msg }
    setHistory(h => [...h, userMsg])
    setLoading(true)

    try {
      const res = await axios.post(API + '/ai/chat', {
        patient_id: context?.patientId || null,
        message: msg,
        history: history.slice(-10),
        context_type: context?.type,
        context_extra: context?.extra || {},
      })
      setHistory(h => [...h, { role: 'assistant', content: res.data.response }])
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 50)
    } catch (err) {
      const raw = err?.response?.data?.detail || err?.message || 'Something went wrong.'
      const status = err?.response?.status
      let msg = raw
      if (!err.response && String(raw).toLowerCase().includes('network')) {
        msg = 'Cannot reach the server right now. Please check your connection and try again.'
      } else if (status === 404 || String(raw).toLowerCase().includes('not found')) {
        msg = 'The AI service endpoint isn\'t available yet — the backend needs to be deployed with the new AI routes.'
      } else if (status === 503 || String(raw).toLowerCase().includes('api_key')) {
        msg = 'Caleb\'s API key is not configured on the server. Ask your admin to add the ANTHROPIC_API_KEY.'
      }
      setHistory(h => [...h, { role: 'assistant', content: ` ${msg}` }])
    } finally {
      setLoading(false)
    }
  }, [input, loading, history, context])

  return (
    <CalebContext.Provider value={{ isOpen, open, close, context, history, input, setInput, loading, send, analysis, loadInjuryAnalysis, endRef }}>
      {children}
    </CalebContext.Provider>
  )
}

export function useCaleb() {
  const ctx = useContext(CalebContext)
  if (!ctx) throw new Error('useCaleb must be used inside <CalebProvider>')
  return ctx
}
